import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';
import {
  Search, Paperclip, Send, Image as ImageIcon, FileText, Download,
  CheckCircle, Clock, X, RefreshCw, User, Maximize2,
  Plus, MessageSquare, AlertCircle, ExternalLink, ChevronDown,
  Trash2, Edit2, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const BASE_URL = (import.meta.env.VITE_REACT_APP_BASE_URL || 'https://diasporawise.com:4000').replace(/\/+$/, '');

const getAccessToken = () => {
  try {
    return localStorage.getItem('diasporawise_admin_token') || localStorage.getItem('diasporawise_user_token') || '';
  } catch (e) {
    return '';
  }
};

const formatTime = (dateStr?: string | Date) => {
  if (!dateStr) return '';
  try {
    const dt = new Date(dateStr);
    return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
};

const formatDateGroupHeader = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }
  } catch (e) {
    return dateStr;
  }
};

interface SupportChatWorkspaceProps {
  user_type?: 'user' | 'dealer';
  title?: string;
}

export default function SupportChatWorkspace({ user_type = 'user', title = 'Support Chat' }: SupportChatWorkspaceProps) {
  const [conversations, setConversations] = useState<any[]>([]);
  const [activeConversation, setActiveConversation] = useState<any | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Edit / Delete state
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');

  // Start New Chat modal state
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [availableClients, setAvailableClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const fetchClientsForNewChat = async () => {
    setLoadingClients(true);
    try {
      const token = getAccessToken();
      if (user_type === 'dealer') {
        const res = await axios.get(`${BASE_URL}/admin/dealers?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dealers = res.data.data?.dealers || (Array.isArray(res.data.data) ? res.data.data : []);
        setAvailableClients(dealers);
      } else {
        const res = await axios.get(`${BASE_URL}/admin/user-list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const users = Array.isArray(res.data.data) ? res.data.data : (res.data.data?.users || []);
        setAvailableClients(users);
      }
    } catch (e) {
      console.error('Error fetching clients for new chat:', e);
      toast.error('Failed to load list of ' + (user_type === 'dealer' ? 'dealers' : 'users'));
    } finally {
      setLoadingClients(false);
    }
  };

  const handleStartChatWithClient = async (client: any) => {
    try {
      const token = getAccessToken();
      const payload: any = {
        user_type,
        subject: 'Direct Admin Inquiry',
      };
      if (user_type === 'dealer') {
        payload.dealer_id = client.dealer_id;
      } else {
        payload.user_id = client.user_id;
      }

      const res = await axios.post(`${BASE_URL}/admin/support/initiate`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.data) {
        const conv = res.data.data;
        setIsNewChatModalOpen(false);
        await fetchConversations();
        handleSelectConversation(conv);
        toast.success('Chat started successfully!');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start chat');
    }
  };

  const filteredClients = availableClients.filter((c) => {
    if (!clientSearchQuery.trim()) return true;
    const q = clientSearchQuery.toLowerCase();
    const name = (c.name || `${c.first_name || ''} ${c.last_name || ''}`).toLowerCase();
    const email = (c.email || '').toLowerCase();
    const phone = (c.mobile_number || c.business_phone || '').toLowerCase();
    const bName = (c.business_name || '').toLowerCase();
    return name.includes(q) || email.includes(q) || phone.includes(q) || bName.includes(q);
  });

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const activeConversationRef = useRef<any>(null);

  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  const access = getAccessToken();

  // Socket connection (initialized once)
  useEffect(() => {
    const socket = io(BASE_URL.replace(/\/admin$|\/api$/, ''), {
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Admin support socket connected');
      if (activeConversationRef.current) {
        socket.emit('join_conversation', activeConversationRef.current.id);
      }
    });

    socket.on('new_support_message', (msg: any) => {
      const currentActive = activeConversationRef.current;
      if (currentActive && Number(msg.conversation_id) === Number(currentActive.id)) {
        setMessages((prev) => {
          if (prev.some((m) => String(m.id) === String(msg.id))) return prev;
          return [...prev, msg];
        });
        markAsRead(currentActive.id);
      }
      fetchConversations();
    });

    socket.on('support_unread_updated', () => {
      fetchConversations();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Fetch Conversations
  const fetchConversations = async () => {
    setLoadingConversations(true);
    try {
      const res = await axios.get(`${BASE_URL}/admin/support/conversations`, {
        params: { user_type, status: statusFilter, search: searchQuery },
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      const list = res.data.data || [];
      setConversations(list);

      if (activeConversationRef.current) {
        const updated = list.find((c: any) => c.id === activeConversationRef.current.id);
        if (updated) setActiveConversation(updated);
      }
    } catch (e) {
      console.error('Error fetching conversations:', e);
    } finally {
      setLoadingConversations(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [user_type, statusFilter, searchQuery]);

  // Fetch Messages for active conversation
  const fetchMessages = async (convId: number) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${BASE_URL}/admin/support/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      setMessages(res.data.data || []);
      markAsRead(convId);
    } catch (e) {
      console.error('Error fetching messages:', e);
    } finally {
      setLoadingMessages(false);
    }
  };

  const markAsRead = async (convId: number) => {
    try {
      await axios.post(
        `${BASE_URL}/admin/support/conversations/${convId}/mark-read`,
        { reader_type: 'admin' },
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, admin_unread_count: 0 } : c))
      );
    } catch (e) {
      console.error('Error marking as read:', e);
    }
  };

  const handleSelectConversation = (conv: any) => {
    const prevConv = activeConversationRef.current;
    setActiveConversation(conv);
    activeConversationRef.current = conv;
    fetchMessages(conv.id);

    if (socketRef.current) {
      if (prevConv) {
        socketRef.current.emit('leave_conversation', prevConv.id);
      }
      socketRef.current.emit('join_conversation', conv.id);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Send Message Handler
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!activeConversation) return;

    let fileData: any = null;

    if (selectedFile) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      try {
        const uploadRes = await axios.post(`${BASE_URL}/admin/support/upload`, formData, {
          headers: {
            Authorization: `Bearer ${access}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        fileData = uploadRes.data.data;
      } catch (err) {
        toast.error('Failed to upload attachment.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    if (!inputMessage.trim() && !fileData) return;

    const payload = {
      message: inputMessage.trim(),
      message_type: fileData ? fileData.message_type : 'text',
      file_url: fileData ? fileData.file_url : undefined,
      file_name: fileData ? fileData.file_name : undefined,
      file_size: fileData ? fileData.file_size : undefined,
      sender_type: 'admin',
      sender_name: 'Support Agent',
    };

    setInputMessage('');
    setSelectedFile(null);

    try {
      const res = await axios.post(
        `${BASE_URL}/admin/support/conversations/${activeConversation.id}/messages`,
        payload,
        { headers: { Authorization: `Bearer ${access}` } }
      );
      if (res.data?.data) {
        const newMsg = res.data.data;
        setMessages((prev) => {
          if (prev.some((m) => String(m.id) === String(newMsg.id))) return prev;
          return [...prev, newMsg];
        });
      }
      fetchConversations();
    } catch (err) {
      toast.error('Failed to send message.');
    }
  };

  // Edit Message Handler
  const startEditMessage = (msg: any) => {
    setEditingMessageId(msg.id);
    setEditingText(msg.message || '');
  };

  const handleSaveEditedMessage = async (msgId: number) => {
    if (!editingText.trim()) return;
    try {
      await axios.put(
        `${BASE_URL}/admin/support/messages/${msgId}`,
        { message: editingText.trim() },
        { headers: { Authorization: `Bearer ${access}` } }
      );
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, message: editingText.trim() } : m))
      );
      setEditingMessageId(null);
      setEditingText('');
      toast.success('Message updated!');
    } catch (err) {
      toast.error('Failed to edit message.');
    }
  };

  // Delete Message Handler
  const handleDeleteMessage = async (msgId: number) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await axios.delete(`${BASE_URL}/admin/support/messages/${msgId}`, {
        headers: { Authorization: `Bearer ${access}` },
      });
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      toast.success('Message deleted!');
    } catch (err) {
      toast.error('Failed to delete message.');
    }
  };

  // Delete Entire Conversation Handler
  const handleDeleteConversation = async (convId: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this chat thread? All messages in this conversation will be permanently removed.')) return;
    try {
      await axios.delete(`${BASE_URL}/admin/support/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${access}` },
      });
      if (activeConversation?.id === convId) {
        setActiveConversation(null);
        setMessages([]);
      }
      fetchConversations();
      toast.success('Chat thread deleted!');
    } catch (err) {
      toast.error('Failed to delete chat thread.');
    }
  };

  // Update Status
  const handleStatusChange = async (newStatus: string) => {
    if (!activeConversation) return;
    try {
      await axios.post(
        `${BASE_URL}/admin/support/conversations/${activeConversation.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${access}` } }
      );
      setActiveConversation({ ...activeConversation, status: newStatus });
      fetchConversations();
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error('Failed to update status.');
    }
  };

  // Client display details
  const getClientName = (conv: any) => {
    if (!conv) return 'Client';
    if (conv.user_type === 'dealer' && conv.dealer) {
      const dName = conv.dealer.name || 'Dealer';
      const bName = conv.dealer.business_name;
      return bName ? `${dName} • ${bName}` : dName;
    }
    if (conv.user) {
      const fn = conv.user.first_name || '';
      const ln = conv.user.last_name || '';
      const full = `${fn} ${ln}`.trim();
      return full || conv.user.name || conv.user.email || 'User';
    }
    return conv.ticket_number || 'Client';
  };

  const getClientContact = (conv: any) => {
    if (!conv) return '';
    const obj = conv.user_type === 'dealer' ? conv.dealer : conv.user;
    if (!obj) return '';
    return obj.mobile_number || obj.email || '';
  };

  const getProfileImageSrc = (conv: any) => {
    if (!conv) return null;
    const obj = conv.user_type === 'dealer' ? conv.dealer : conv.user;
    if (!obj || !obj.profile_image) return null;
    const img = obj.profile_image;
    if (img.startsWith('http')) return img;
    return `${BASE_URL.replace(/\/admin$|\/api$/, '')}${img.startsWith('/') ? '' : '/'}${img}`;
  };

  const getFileFullUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const origin = BASE_URL.replace(/\/admin$|\/api$/, '');
    return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">Open</span>;
      case 'in_progress':
        return <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">In Progress</span>;
      case 'resolved':
        return <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Resolved</span>;
      case 'closed':
        return <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-700 border border-gray-200">Closed</span>;
      default:
        return <span className="px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-50 rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Top Banner */}
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md">
            <MessageSquare size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500">Manage real-time support tickets and client messages</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => {
              setIsNewChatModalOpen(true);
              fetchClientsForNewChat();
            }}
            className="flex items-center space-x-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-xs transition-colors shadow-sm cursor-pointer"
          >
            <Plus size={16} />
            <span>Start New Chat</span>
          </button>
          <button
            type="button"
            onClick={fetchConversations}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-xl transition-colors"
            title="Refresh List"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Main Workspace Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT PANEL: Conversation List */}
        <div className="w-80 md:w-96 border-r border-gray-200 bg-white flex flex-col shrink-0">
          {/* Search & Filter Bar */}
          <div className="p-3.5 border-b border-gray-200 bg-gray-50/50 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search ticket, name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs text-gray-900 bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
              {['all', 'open', 'in_progress', 'resolved', 'closed'].map((st) => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-lg capitalize whitespace-nowrap transition-colors ${statusFilter === st
                      ? 'bg-blue-600 text-white font-bold shadow-xs'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {st.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Ticket Threads */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {loadingConversations ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-400 space-y-2">
                <RefreshCw className="animate-spin text-blue-600" size={24} />
                <span className="text-sm">Loading support threads...</span>
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-gray-400 space-y-2">
                <MessageSquare size={32} className="text-gray-300" />
                <p className="text-sm font-medium text-gray-500">No chat threads found</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const isActive = activeConversation?.id === conv.id;
                const clientName = getClientName(conv);
                const convImg = getProfileImageSrc(conv);

                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`p-3.5 cursor-pointer transition-all duration-150 relative ${isActive
                        ? 'bg-blue-50/90 border-l-4 border-blue-600 shadow-inner'
                        : 'hover:bg-gray-100 bg-white'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center space-x-2.5">
                        {convImg ? (
                          <img
                            src={convImg}
                            alt={clientName}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200 shadow-sm shrink-0"
                            onError={(e: any) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                            {clientName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 line-clamp-1">{clientName}</h4>
                          <span className="text-[11px] text-gray-400 font-mono">{conv.ticket_number}</span>
                        </div>
                      </div>
                      <span className="text-[10px] text-gray-400 font-medium">
                        {formatTime(conv.last_message_at || conv.created_at)}
                      </span>
                    </div>

                    <p className="text-xs text-gray-600 line-clamp-1 mb-2 pl-11">
                      {conv.last_message || 'No messages yet'}
                    </p>

                    <div className="flex items-center justify-between pl-11">
                      {getStatusBadge(conv.status)}

                      <div className="flex items-center space-x-2">
                        {conv.admin_unread_count > 0 && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white shadow-sm animate-pulse">
                            {conv.admin_unread_count} new
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => handleDeleteConversation(conv.id, e)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Chat Thread"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Active Chat Thread */}
        <div className="flex-1 flex flex-col bg-white">
          {activeConversation ? (
            <>
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center space-x-3">
                  {getProfileImageSrc(activeConversation) ? (
                    <img
                      src={getProfileImageSrc(activeConversation)!}
                      alt={getClientName(activeConversation)}
                      className="w-10 h-10 rounded-full object-cover border border-gray-200 shadow shrink-0"
                      onError={(e: any) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-base shadow shrink-0">
                      {getClientName(activeConversation).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-base font-bold text-gray-800">
                        {getClientName(activeConversation)}
                      </h3>
                      <span className="text-xs text-gray-400 font-mono">
                        ({activeConversation.ticket_number})
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {getClientContact(activeConversation)}
                    </p>
                  </div>
                </div>

                {/* Details Links, Delete Button & Status Dropdown */}
                <div className="flex items-center space-x-3">
                  {(user_type === 'user' || activeConversation.user_type === 'user') && (activeConversation.user_id || activeConversation.user?.user_id || activeConversation.user?.id) && (
                    <Link
                      to={`/admin/user-details/${activeConversation.user_id || activeConversation.user?.user_id || activeConversation.user?.id}`}
                      className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                    >
                      <span>User Details</span>
                      <ExternalLink size={12} />
                    </Link>
                  )}
                  {(user_type === 'dealer' || activeConversation.user_type === 'dealer') && (activeConversation.dealer_id || activeConversation.dealer?.dealer_id || activeConversation.dealer?.id) && (
                    <Link
                      to={`/admin/dealer-management/${activeConversation.dealer_id || activeConversation.dealer?.dealer_id || activeConversation.dealer?.id}`}
                      className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors"
                    >
                      <span>Dealer Details</span>
                      <ExternalLink size={12} />
                    </Link>
                  )}

                  {/* Delete Conversation */}
                  <button
                    type="button"
                    onClick={(e) => handleDeleteConversation(activeConversation.id, e)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
                    title="Delete Chat Thread"
                  >
                    <Trash2 size={13} />
                    <span>Delete Chat</span>
                  </button>

                  {/* Status Dropdown */}
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-gray-500 font-medium">Status:</span>
                    <select
                      value={activeConversation.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs cursor-pointer"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                {loadingMessages ? (
                  <div className="flex justify-center p-8 text-gray-400">
                    <RefreshCw className="animate-spin text-blue-600" size={24} />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                    <MessageSquare size={40} className="text-gray-300 mb-2" />
                    <p className="text-sm font-medium">No messages yet</p>
                    <p className="text-xs">Type a message below to start the conversation.</p>
                  </div>
                ) : (
                  (() => {
                    let lastDateStr = '';
                    const clientImg = getProfileImageSrc(activeConversation);
                    const clientInitial = getClientName(activeConversation).charAt(0).toUpperCase();

                    return messages.map((msg) => {
                      const isAdmin = msg.sender_type === 'admin';
                      const msgDateStr = msg.created_at ? new Date(msg.created_at).toDateString() : '';
                      const showDateHeader = msgDateStr !== lastDateStr;
                      if (showDateHeader) lastDateStr = msgDateStr;

                      return (
                        <React.Fragment key={msg.id}>
                          {showDateHeader && (
                            <div className="flex justify-center my-3">
                              <span className="px-3 py-1 text-[11px] font-semibold text-gray-500 bg-gray-200/80 rounded-full shadow-xs">
                                {formatDateGroupHeader(msg.created_at)}
                              </span>
                            </div>
                          )}

                          <div className={`flex items-end gap-2 ${isAdmin ? 'flex-row-reverse' : 'flex-row'}`}>
                            {/* Client Avatar */}
                            {!isAdmin && (
                              <div className="shrink-0 mb-1">
                                {clientImg ? (
                                  <img
                                    src={clientImg}
                                    alt={getClientName(activeConversation)}
                                    className="w-8 h-8 rounded-full object-cover border border-gray-200 shadow-sm"
                                    onError={(e: any) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                                    {clientInitial}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Bubble */}
                            <div className={`flex flex-col max-w-[72%] ${isAdmin ? 'items-end' : 'items-start'} group relative`}>
                              <div
                                className={`rounded-2xl p-3.5 shadow-sm transition-all w-full ${isAdmin
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-br-none'
                                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                                  }`}
                              >
                                <div className="flex items-center justify-between mb-1 space-x-3 text-[11px] opacity-75">
                                  <span className="font-semibold">
                                    {isAdmin
                                      ? (msg.sender_name || 'Support Agent')
                                      : getClientName(activeConversation)}
                                  </span>
                                  <span>{formatTime(msg.created_at)}</span>
                                </div>

                                {/* Message Text or Inline Edit */}
                                {editingMessageId === msg.id ? (
                                  <div className="mt-1 flex items-center space-x-2">
                                    <input
                                      type="text"
                                      value={editingText}
                                      onChange={(e) => setEditingText(e.target.value)}
                                      className="px-2.5 py-1 text-xs text-gray-900 bg-white rounded border border-blue-400 focus:outline-none flex-1 shadow-inner"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveEditedMessage(msg.id);
                                        if (e.key === 'Escape') setEditingMessageId(null);
                                      }}
                                    />
                                    <button
                                      onClick={() => handleSaveEditedMessage(msg.id)}
                                      className="p-1 bg-green-500 hover:bg-green-600 text-white rounded shadow-sm transition-colors"
                                      title="Save Changes"
                                    >
                                      <Check size={14} />
                                    </button>
                                    <button
                                      onClick={() => setEditingMessageId(null)}
                                      className="p-1 bg-gray-400 hover:bg-gray-500 text-white rounded shadow-sm transition-colors"
                                      title="Cancel"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  msg.message && (
                                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                                  )
                                )}

                                {/* Image Attachment */}
                                {(msg.message_type === 'image' || (msg.file_url && /\.(jpg|jpeg|png|webp|gif|heic|heif)$/i.test(msg.file_url))) && msg.file_url && (
                                  <div className="mt-2 group/img relative rounded-lg overflow-hidden border border-black/10 bg-black/5">
                                    <img
                                      src={getFileFullUrl(msg.file_url)}
                                      alt="Attachment"
                                      className="max-h-60 rounded-lg object-cover cursor-pointer transition-transform group-hover/img:scale-105"
                                      onClick={() => setPreviewImage(getFileFullUrl(msg.file_url))}
                                    />
                                    <div
                                      onClick={() => setPreviewImage(getFileFullUrl(msg.file_url))}
                                      className="absolute inset-0 bg-black/30 opacity-0 group-hover/img:opacity-100 flex items-center justify-center text-white cursor-pointer transition-opacity"
                                    >
                                      <Maximize2 size={24} />
                                    </div>
                                  </div>
                                )}

                                {/* File Attachment */}
                                {msg.message_type === 'file' && msg.file_url && (
                                  <div className={`mt-2 flex items-center justify-between p-3 rounded-lg border text-xs ${isAdmin ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'
                                    }`}>
                                    <div className="flex items-center space-x-2 truncate">
                                      <FileText size={20} className="shrink-0" />
                                      <div className="truncate">
                                        <p className="font-medium truncate">{msg.file_name || 'Attachment File'}</p>
                                        {msg.file_size && (
                                          <p className="text-[10px] opacity-75">{(msg.file_size / 1024).toFixed(1)} KB</p>
                                        )}
                                      </div>
                                    </div>
                                    <a
                                      href={`${BASE_URL.replace(/\/admin$|\/api$/, '')}${msg.file_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download
                                      className={`ml-3 p-1.5 rounded-full hover:bg-black/10 transition-colors ${isAdmin ? 'text-white' : 'text-blue-600'}`}
                                    >
                                      <Download size={16} />
                                    </a>
                                  </div>
                                )}
                              </div>

                              {/* Hover Actions (Edit / Delete) */}
                              <div className={`flex items-center space-x-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                                {msg.message && editingMessageId !== msg.id && (
                                  <button
                                    onClick={() => startEditMessage(msg)}
                                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-white rounded transition-colors"
                                    title="Edit Message"
                                  >
                                    <Edit2 size={12} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-white rounded transition-colors"
                                  title="Delete Message"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Selected File Badge */}
              {selectedFile && (
                <div className="px-5 py-2 bg-blue-50 border-t border-blue-100 flex items-center justify-between text-xs text-blue-700 font-medium">
                  <div className="flex items-center space-x-2 truncate">
                    <Paperclip size={14} />
                    <span className="truncate">{selectedFile.name}</span>
                    <span className="text-[10px] opacity-75">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="p-1 text-blue-500 hover:text-red-600 rounded"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Chat Input Footer */}
              <form onSubmit={handleSendMessage} className="p-3.5 border-t border-gray-200 bg-white flex items-center space-x-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedFile(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-xl transition-colors shrink-0"
                  title="Attach Image or File"
                >
                  <Paperclip size={18} />
                </button>

                <input
                  type="text"
                  placeholder="Type your response..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  className="flex-1 px-4 py-2.5 text-sm text-gray-900 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />

                <button
                  type="submit"
                  disabled={uploading || (!inputMessage.trim() && !selectedFile)}
                  className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-xs rounded-xl hover:shadow-md transition-all disabled:opacity-50 flex items-center space-x-1.5 shrink-0"
                >
                  {uploading ? (
                    <RefreshCw className="animate-spin" size={16} />
                  ) : (
                    <>
                      <span>Send</span>
                      <Send size={14} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-400 bg-gray-50/50">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-500 mb-3 shadow-inner">
                <MessageSquare size={32} />
              </div>
              <h3 className="text-base font-bold text-gray-700">Select a Support Ticket</h3>
              <p className="text-xs text-gray-500 max-w-xs mt-1">
                Choose a conversation from the left sidebar to view messages and respond.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Image Lightbox Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-xs"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] bg-transparent" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-red-400 rounded-full transition-colors"
            >
              <X size={28} />
            </button>
            <img
              src={previewImage}
              alt="Attachment Full View"
              className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Start New Chat Modal */}
      {isNewChatModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageSquare size={18} />
                <h3 className="text-base font-bold">Start New Chat ({user_type === 'dealer' ? 'Dealer' : 'User'})</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsNewChatModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body: Search Bar */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3.5 top-3 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder={`Search ${user_type === 'dealer' ? 'dealer name, business, email...' : 'user name, email, phone...'}`}
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-xs text-gray-900 bg-white rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs"
                  autoFocus
                />
              </div>
            </div>

            {/* Client List */}
            <div className="flex-1 overflow-y-auto p-4 divide-y divide-gray-100 max-h-96">
              {loadingClients ? (
                <div className="flex flex-col items-center justify-center p-8 text-gray-400 space-y-2">
                  <RefreshCw className="animate-spin text-blue-600" size={24} />
                  <span className="text-sm">Loading available {user_type === 'dealer' ? 'dealers' : 'users'}...</span>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No matching {user_type === 'dealer' ? 'dealers' : 'users'} found.
                </div>
              ) : (
                filteredClients.map((client) => {
                  const displayName = user_type === 'dealer'
                    ? (client.business_name ? `${client.name} • ${client.business_name}` : client.name)
                    : (`${client.first_name || ''} ${client.last_name || ''}`.trim() || client.name || client.email);
                  const contact = client.email || client.mobile_number || client.business_phone || '';
                  const initial = (displayName || 'C').charAt(0).toUpperCase();

                  return (
                    <div
                      key={client.dealer_id || client.user_id || client.id}
                      onClick={() => handleStartChatWithClient(client)}
                      className="py-3 px-3 hover:bg-blue-50 rounded-xl cursor-pointer flex items-center justify-between transition-colors group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                          {initial}
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {displayName}
                          </h4>
                          <p className="text-xs text-gray-500">{contact}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-xs cursor-pointer"
                      >
                        Start Chat
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
